"use client"

import React, { useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { doctorSignupWithDocs, doctorSignup, ApiError } from "@/lib/api"
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input"
import { MultiDocumentUpload } from "@/components/multi-document-upload"

import { toast } from "sonner"

import {
  Eye,
  EyeOff,
  User,
  Mail,
  Building,
  MapPin,
  Award,
  Lock,
} from "lucide-react"

type FieldErrors = {
  fullName?: string
  email?: string
  phoneNumber?: string
  specialization?: string
  clinicName?: string
  clinicAddress?: string
  licenseNumber?: string
  commissionPct?: string
  password?: string
  confirmPassword?: string
  documents?: string   
}

/* -------------------- Shared Components -------------------- */

const ErrorText = ({ text }: { text?: string }) =>
  text ? <p className="text-sm text-red-500 mt-1">{text}</p> : null

const InputField = ({
  label,
  icon: Icon,
  value,
  onChange,
  placeholder,
  type = "text",
  error,
  required = false,
}: {
  label: string
  icon: React.ElementType
  value: string
  onChange: (value: string) => void
  placeholder?: string
  type?: React.HTMLInputTypeAttribute
  error?: string
  required?: boolean
}) => {
  return (
    <div>
      <Label className="text-sm font-medium text-foreground">
        {label} {required && <span className="text-primary">*</span>}
      </Label>

      <div className="relative mt-1.5">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>

        <Input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`pl-10 h-11 ${error ? "border-red-500" : ""}`}
        />
      </div>

      <ErrorText text={error} />
    </div>
  )
}

const PasswordField = ({
  label,
  value,
  onChange,
  placeholder,
  error,
  show,
  onToggle,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  error?: string
  show: boolean
  onToggle: () => void
}) => {
  return (
    <div>
      <Label className="text-sm font-medium text-foreground">
        {label}
      </Label>

      <div className="relative mt-1.5">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Lock className="h-4 w-4 text-muted-foreground" />
        </div>

        <Input
          type={show ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`pl-10 pr-10 h-11 ${error ? "border-red-500" : ""}`}
        />

        <button
          type="button"
          onClick={onToggle}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          {show ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>

      <ErrorText text={error} />
    </div>
  )
}

/* -------------------- Phone Field Component -------------------- */

const PhoneField = ({
  value,
  onChange,
  error,
}: {
  value: string | undefined
  onChange: (value: string | undefined) => void
  error?: string
}) => {
  return (
    <div>
      <Label className="text-sm font-medium text-foreground">
        Phone Number <span className="text-primary">*</span>
      </Label>

      <div
        className={`mt-1.5 flex items-center h-11 w-full rounded-md border bg-background px-3 text-sm
          ring-offset-background transition-colors
          focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2
          ${error ? "border-red-500" : "border-input"}`}
      >
        <PhoneInput
          international
          defaultCountry="IN"
          value={value}
          onChange={onChange}
          className="flex w-full items-center gap-2"
          numberInputProps={{
            className:
              "flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground",
          }}
        />
      </div>

      <ErrorText text={error} />
    </div>
  )
}

/* -------------------- Main Component -------------------- */

export function SignupForm() {
  const router = useRouter()

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phoneNumber: undefined as string | undefined,
    specialization: "",
    clinicName: "",
    clinicAddress: "",
    licenseNumber: "",
    commissionPct: "",
    documents: [] as File[],
    password: "",
    confirmPassword: "",
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const setValue = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  const setDocuments = (files: File[]) => {
    setForm((prev) => ({ ...prev, documents: files }))   // ✅ direct File[] store
    setFieldErrors((prev) => ({ ...prev, documents: undefined }))
  }

  const handleDoctorSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setFieldErrors({})

    const errors: FieldErrors = {}

    if (!form.fullName.trim()) errors.fullName = "Full name is required"
    if (!form.email.trim()) errors.email = "Email is required"

    if (!form.phoneNumber) {
      errors.phoneNumber = "Phone number is required"
    } else if (!isValidPhoneNumber(form.phoneNumber)) {
      errors.phoneNumber = "Enter a valid phone number"
    }

    if (form.commissionPct) {
      const val = parseFloat(form.commissionPct)
      if (isNaN(val) || val < 0 || val > 100) {
        errors.commissionPct = "Commission % must be between 0 and 100"
      }
    }

    if (!form.password) errors.password = "Password is required"
    if (form.password !== form.confirmPassword)
      errors.confirmPassword = "Passwords do not match"

    if (!form.documents?.length) {
      errors.documents = "At least one document is required"
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }

    setIsLoading(true)

    try {
      const signupData = {
        fullName: form.fullName,
        email: form.email,
        phoneNumber: form.phoneNumber ?? '',
        specialization: form.specialization,
        clinicName: form.clinicName,
        clinicAddress: form.clinicAddress,
        licenseNumber: form.licenseNumber,
        commissionPct: form.commissionPct ? parseFloat(form.commissionPct) : undefined,
        password: form.password,
        documents: form.documents
      };

      await doctorSignup(signupData);

      toast.success("Registration successful! Please login with your credentials.");

      setTimeout(() => {
        router.push("/doctor/login")
      }, 3000)

    } catch (err: any) {
      if (err?.message && Array.isArray(err.message)) {
        const mappedErrors: FieldErrors = {}

        err.message.forEach((msg: string) => {
          const m = msg.toLowerCase()
          if (m.includes("documents")) mappedErrors.documents = msg
          else if (m.includes("email")) mappedErrors.email = msg
          else if (m.includes("password")) mappedErrors.password = msg
          else if (m.includes("phone")) mappedErrors.phoneNumber = msg
          else if (m.includes("name")) mappedErrors.fullName = msg
          else if (m.includes("clinic name")) mappedErrors.clinicName = msg
          else if (m.includes("clinic address")) mappedErrors.clinicAddress = msg
          else if (m.includes("license")) mappedErrors.licenseNumber = msg
          else if (m.includes("specialization")) mappedErrors.specialization = msg
          else if (m.includes("commission")) mappedErrors.commissionPct = msg
          else setError(msg)
        })

        setFieldErrors(mappedErrors)
      } else if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError("Something went wrong")
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleDoctorSignup} className="space-y-5">

      {error && (
        <div className="p-4 text-sm text-red-500 bg-red-50 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      {/* Personal Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Full Name"
          icon={User}
          value={form.fullName}
          onChange={(v) => setValue("fullName", v)}
          placeholder="Dr. Rajesh Sharma"
          error={fieldErrors.fullName}
          required
        />

        <InputField
          label="Email"
          icon={Mail}
          type="email"
          value={form.email}
          onChange={(v) => setValue("email", v)}
          placeholder="doctor@example.com"
          error={fieldErrors.email}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PhoneField
          value={form.phoneNumber}
          onChange={(v) => setValue("phoneNumber", v)}
          error={fieldErrors.phoneNumber}
        />

        <InputField
          label="Specialization"
          icon={Award}
          value={form.specialization}
          onChange={(v) => setValue("specialization", v)}
          placeholder="Cardiologist"
        />
      </div>

      {/* Clinic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Clinic Name"
          icon={Building}
          value={form.clinicName}
          onChange={(v) => setValue("clinicName", v)}
          placeholder="HealthCare Clinic"
          error={fieldErrors.clinicName}
        />

        <InputField
          label="Clinic Address"
          icon={MapPin}
          value={form.clinicAddress}
          onChange={(v) => setValue("clinicAddress", v)}
          placeholder="Yamunanagar, Haryana"
          error={fieldErrors.clinicAddress}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="License Number"
          icon={Award}
          value={form.licenseNumber}
          onChange={(v) => setValue("licenseNumber", v)}
          placeholder="DOC-HR-556677"
          error={fieldErrors.licenseNumber}
        />

        <InputField
          label="Commission Percentage"
          icon={Award}
          type="number"
          value={form.commissionPct}
          onChange={(v) => setValue("commissionPct", v)}
          placeholder="e.g. 10"
          error={fieldErrors.commissionPct}
        />
      </div>

      <MultiDocumentUpload
        files={form.documents}
        onFilesChange={(files: File[]) => setForm(prev => ({ ...prev, documents: files }))}
        required
      />

      {/* Password */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PasswordField
          label="Password"
          value={form.password}
          onChange={(v) => setValue("password", v)}
          placeholder="Create strong password"
          error={fieldErrors.password}
          show={showPassword}
          onToggle={() => setShowPassword(!showPassword)}
        />

        <PasswordField
          label="Confirm Password"
          value={form.confirmPassword}
          onChange={(v) => setValue("confirmPassword", v)}
          placeholder="Re-enter password"
          error={fieldErrors.confirmPassword}
          show={showConfirmPassword}
          onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
        />
      </div>

      {/* Terms */}
      <div className="flex items-start space-x-2">
        <Checkbox
          checked={agreeTerms}
          onCheckedChange={(v) => setAgreeTerms(v as boolean)}
        />
        <label className="text-sm text-muted-foreground">
          I agree to the{" "}
          <Link href="/terms" className="text-primary hover:underline">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link href="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
        </label>
      </div>

      <Button
        type="submit"
        disabled={isLoading || !agreeTerms}
        className="w-full h-12"
      >
        {isLoading ? "Registering doctor..." : "Register Doctor"}
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link href="/doctor/login" className="text-primary hover:underline">
          Sign in
        </Link>
      </p>
    </form>
  )
}